/**
 * Per-job row expandable — rediseñado para que el ojo vaya en este orden:
 *
 *   L1: Blueprint name (bold) → product (mono)
 *   L2: Progress bar (full width, accent/muted/yellow)
 *   L3: ETA + Status badge
 *   L4: Valuation a la derecha (margin/value) — alineado a la derecha del todo
 *   L5: Footer con ESI/SDE chip + hint de "click para ver materials"
 *
 * La idea: el nombre Y la barra de progreso son lo que más mirás.
 * El margin es importante pero secundario — va a la derecha sin competir.
 *
 * Expand: tabla de materials por run (sin cambios respecto a la versión previa,
 * porque el contenido era correcto, sólo le faltaba espacio visual).
 */

import { formatISK, formatISKShort } from '../api'
import { IndustryStatusBadge } from './IndustryStatusBadge'
import {
  formatDuration,
  isDone,
  type Material,
  type ValuedJob,
} from '../lib/industry'

export function IndustryJobRow({ job, now }: { job: ValuedJob; now: number }) {
  const done = isDone(job.status)
  const endMs = new Date(job.end_date).getTime()
  const startMs = new Date(job.start_date).getTime()
  const total = Math.max(1, endMs - startMs)
  const liveProgress = done ? 1 : Math.min(1, Math.max(0, (now - startMs) / total))
  const liveRemainingSec = Math.max(0, Math.round((endMs - now) / 1000))

  const hasMargin = job.margin_total !== null
  const hasValue = job.expected_total_value !== null

  const marginPositive = (job.margin_total ?? 0) > 0
  const marginNegative = (job.margin_total ?? 0) < 0

  return (
    <details className="border border-eve-border rounded-md bg-eve-panel/30 group hover:border-eve-border/80 transition-colors">
      <summary className="cursor-pointer p-3 hover:bg-eve-panel/40 transition-colors list-none">
        {/* L1: nombre + producto */}
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-eve-text truncate">{job.blueprint_name}</span>
          <span className="text-xs text-eve-muted">{job.activity_name}</span>
          {job.runs > 1 && (
            <span className="text-xs text-eve-muted font-mono">× {job.runs}</span>
          )}
          {job.product_name && (
            <span className="text-xs text-eve-muted-2 font-mono">
              → {job.product_name}
            </span>
          )}
        </div>

        {/* L2: progress bar — full width, pegada al nombre */}
        <div className="mt-2 h-1.5 bg-eve-bg rounded overflow-hidden">
          <div
            className={`h-full transition-all ${
              done
                ? 'bg-eve-muted'
                : job.status === 'paused'
                  ? 'bg-yellow-500'
                  : 'bg-eve-accent'
            }`}
            style={{ width: `${liveProgress * 100}%` }}
          />
        </div>

        {/* L3 + L4: ETA + status a la izquierda, valuation a la derecha */}
        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <IndustryStatusBadge status={job.status} />
            {!done && (
              <span className="text-eve-muted font-mono">
                Termina en {formatDuration(liveRemainingSec)}
              </span>
            )}
            {done && (
              <span
                className="text-eve-muted font-mono"
                title={new Date(job.end_date).toISOString()}
              >
                {formatEveUtc(new Date(job.end_date))}
              </span>
            )}
          </div>

          {/* Valuation column */}
          <div className="text-right">
            {hasMargin ? (
              <div className="flex items-baseline gap-2 justify-end">
                <span
                  className={`text-sm font-mono font-semibold ${
                    marginPositive
                      ? 'text-eve-success'
                      : marginNegative
                        ? 'text-eve-danger'
                        : 'text-eve-text'
                  }`}
                  title="Margin = output Jita sell - materials - facility tax"
                >
                  {marginPositive ? '+' : ''}
                  {formatISK(job.margin_total!)} ISK
                </span>
                {job.margin_pct != null && (
                  <span
                    className={`text-[10px] font-mono ${
                      marginPositive
                        ? 'text-eve-success'
                        : marginNegative
                          ? 'text-eve-danger'
                          : 'text-eve-muted'
                    }`}
                  >
                    {marginPositive ? '+' : ''}
                    {job.margin_pct.toFixed(1)}%
                  </span>
                )}
                {job.margin_per_hour !== null && !done && (
                  <span className="text-[10px] font-mono text-eve-accent font-semibold">
                    {formatISKShort(job.margin_per_hour)}/h
                  </span>
                )}
              </div>
            ) : hasValue ? (
              <div className="flex items-baseline gap-2 justify-end">
                <span
                  className="text-sm font-mono font-semibold text-eve-accent"
                  title="Valor a Jita sell"
                >
                  {formatISK(job.expected_total_value!)} ISK
                </span>
                {job.expected_value_per_hour !== null && !done && (
                  <span className="text-[10px] text-eve-muted font-mono">
                    {formatISKShort(job.expected_value_per_hour)}/h
                  </span>
                )}
              </div>
            ) : job.product_type_id !== null ? (
              <span className="text-xs text-eve-muted italic">sin precio</span>
            ) : (
              <span className="text-xs text-eve-muted italic">—</span>
            )}
          </div>
        </div>

        {/* L5: footer con materials source + hint */}
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px]">
          <div className="flex items-center gap-2">
            {job.materials_source && (
              <span
                className={`font-mono uppercase px-1 py-0.5 rounded ${
                  job.materials_source === 'esi'
                    ? 'bg-eve-info/15 text-eve-info'
                    : 'bg-eve-muted/10 text-eve-muted'
                }`}
                title={
                  job.materials_source === 'esi'
                    ? 'Materials con bonuses aplicados (ESI /characters/{id}/industry/jobs/{id}/materials/)'
                    : 'Materials base del SDE sin bonuses (Fuzzwork)'
                }
              >
                {job.materials_source === 'esi' ? 'ESI' : 'SDE'}
              </span>
            )}
            {job.material_cost_per_run !== null && hasMargin && (
              <span className="text-eve-muted-2 font-mono italic">
                mat {formatISKShort(job.material_cost_per_run)}/run
              </span>
            )}
            {job.materials_note && (
              <span className="text-eve-warn italic">{job.materials_note}</span>
            )}
          </div>
          {hasMargin && job.materials && job.materials.length > 0 && (
            <span className="text-eve-muted-2 uppercase tracking-wider group-open:hidden">
              click para ver materials ▾
            </span>
          )}
          {hasMargin && job.materials && job.materials.length > 0 && (
            <span className="text-eve-muted-2 uppercase tracking-wider hidden group-open:inline">
              ocultar materials ▴
            </span>
          )}
        </div>
      </summary>

      {/* Expandable materials breakdown */}
      {hasMargin && job.materials && job.materials.length > 0 && (
        <MaterialsBreakdown
          job={job}
          marginPositive={marginPositive}
          marginNegative={marginNegative}
        />
      )}
    </details>
  )
}

/**
 * Tabla de materials dentro del row expandible.
 * Header: Output / Mat cost / Tax / Margin/run. Body: por material.
 *
 * Solo se renderiza si el job tiene materials (manufacturing o reaction).
 * Research jobs no tienen materials concept.
 */
function MaterialsBreakdown({
  job,
  marginPositive,
  marginNegative,
}: {
  job: ValuedJob
  marginPositive: boolean
  marginNegative: boolean
}) {
  const materials: Material[] = job.materials ?? []
  return (
    <div className="border-t border-eve-border px-3 py-3 text-xs space-y-2 bg-eve-bg/40">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <span className="stat-label">Materials breakdown (per run)</span>
        <div className="flex gap-3 font-mono text-[11px]">
          <span className="text-eve-muted">
            Output:{' '}
            <span className="text-eve-accent">
              {formatISKShort(job.product_jita_sell ?? 0)} ISK
            </span>
          </span>
          <span className="text-eve-muted">
            Mat cost:{' '}
            <span className="text-eve-danger">
              -{formatISKShort(job.material_cost_per_run ?? 0)} ISK
            </span>
          </span>
          {(job.facility_cost_total ?? 0) > 0 && (
            <span className="text-eve-muted">
              Tax:{' '}
              <span className="text-eve-warn">
                -
                {formatISKShort(
                  (job.facility_cost_total ?? 0) / Math.max(1, job.runs),
                )}{' '}
                ISK
              </span>
            </span>
          )}
          <span
            className={
              marginPositive
                ? 'text-eve-success font-semibold'
                : marginNegative
                  ? 'text-eve-danger font-semibold'
                  : 'text-eve-text font-semibold'
            }
          >
            = {marginPositive ? '+' : ''}
            {formatISKShort(job.margin_per_run ?? 0)}/run
          </span>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="text-eve-muted">
          <tr className="text-left">
            <th className="font-normal pb-1">Material</th>
            <th className="font-normal pb-1 text-right">Qty</th>
            <th className="font-normal pb-1 text-right">Jita buy</th>
            <th className="font-normal pb-1 text-right">Line cost</th>
          </tr>
        </thead>
        <tbody>
          {materials.map((m) => (
            <tr key={m.type_id} className="border-t border-eve-border/40">
              <td className="py-1 truncate" title={m.name}>
                {m.name}
              </td>
              <td className="py-1 text-right font-mono">
                {m.quantity.toLocaleString('en-US')}
              </td>
              <td className="py-1 text-right font-mono">
                {m.jita_buy !== null ? formatISKShort(m.jita_buy) : '—'}
              </td>
              <td className="py-1 text-right font-mono">
                {formatISKShort(m.line_cost)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Format timestamp as EVE UTC ("20 Sep 14:32 UTC"). Usado en rows
 *  completados para que el user sepa la hora exacta sin depender del
 *  locale del browser. Match con UpcomingEvents.tsx. */
function formatEveUtc(d: Date): string {
  const day = d.getUTCDate().toString().padStart(2, '0')
  const month = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ][d.getUTCMonth()]
  const hh = d.getUTCHours().toString().padStart(2, '0')
  const mm = d.getUTCMinutes().toString().padStart(2, '0')
  return `${day} ${month} ${hh}:${mm} UTC`
}
